import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { Content, Subtask, TaskFormData, StepType } from './model/types'
import { useTaskPublication } from './hooks/useTaskPublication'
import { useTaskPdf } from './hooks/useTaskPdf'

// Compatibility entry for existing widgets and the independent AI/page flows.
export * from './model/types'

export type SubtaskActions = {
  /** Dual View keeps AI setup inside the subtask editor rather than the modal step stack. */
  startAiSetup?: (index: number, values: { title?: string; sub_tasks?: any[] }) => void
  append?: (data: Subtask | Content) => void
  remove?: (index: number) => void
  move?: (from: number, to: number) => void
  /**
   * Live snapshot of the step-1 RHF form. The footer sits outside that form's `FormContext`, and
   * entering the AI sub-flow unmounts the step — so whatever is typed has to be read from here and
   * persisted first (see `useVmAiEnhance.openForSubtask`).
   *
   * Deliberately narrower than RHF's `getValues`: this slot is a snapshot, not a general read API.
   */
  snapshotSubtasks?: () => { title?: string; sub_tasks: Subtask[] }
}

type FormStepContextType = {
  isSubtaskAiGenerating?: boolean
  setSubtaskAiGenerating?: React.Dispatch<React.SetStateAction<boolean>>
  currentFormSubmit: (() => void) | null
  setCurrentFormSubmit: React.Dispatch<React.SetStateAction<(() => void) | null>>
  formData: Partial<TaskFormData>
  /**
   * Reads latest formData from a ref — no subscription, does NOT cause re-renders.
   * This is the last *committed* snapshot: step 1 only writes into it on `sumbitStep()`, so for live,
   * mid-typing step-1 state use `subtaskActions.snapshotSubtasks` instead.
   */
  getFormData?: () => Partial<TaskFormData>
  updateFormData: <T extends keyof TaskFormData>(step: T, data: TaskFormData[T]) => void
  stepStack: string[]
  setStepStack: React.Dispatch<React.SetStateAction<string[]>>
  sumbitStep: () => void
  goToPreviousStep: () => void
  submitAllForms: () => void
  generatePdf?: () => Promise<void>
  isPdfGenerating?: boolean
  subtaskActions: SubtaskActions
  setSubtaskActions: React.Dispatch<React.SetStateAction<SubtaskActions>>
  isMediaUploading: boolean
  setIsMediaUploading: React.Dispatch<React.SetStateAction<boolean>>
  isSubmitting: boolean
  publicationComplete?: boolean
  isEdit: boolean
  /** Persisted task deadline at session start; live draft edits must not change the edit exemption. */
  originalDeadline?: string | null
  maxAllowedOverdueDays: number
  setMaxAllowedOverdueDays: React.Dispatch<React.SetStateAction<number>>
  isVmTask: boolean
  isAiTask: boolean
}

export const FormStepContext = createContext<FormStepContextType | null>(null)

export const FormStepProvider: React.FC<{
  children: React.ReactNode
  onClose?: VoidFunction
  initialData?: Partial<TaskFormData>
  isEdit: boolean
  tid?: string
  isVmTask?: boolean
  isAiTask?: boolean
  isGroupedByCategory?: boolean
}> = ({
  children,
  onClose,
  initialData = {},
  isEdit,
  tid,
  isVmTask = false,
  isAiTask = false,
  isGroupedByCategory = false,
}) => {
  const [isSubtaskAiGenerating, setSubtaskAiGenerating] = useState(false)
  const [currentFormSubmit, setCurrentFormSubmit] = useState<(() => void) | null>(null)
  const [formData, setFormData] = useState<Partial<TaskFormData>>(initialData)
  const [stepStack, setStepStack] = useState<string[]>([StepType.SUBTASK_TASK_CREATION])
  const [isMediaUploading, setIsMediaUploading] = useState(false)
  const [maxAllowedOverdueDays, setMaxAllowedOverdueDays] = useState<number>(61)

  const [subtaskActions, setSubtaskActions] = useState<SubtaskActions>({})

  const formDataRef = useRef<Partial<TaskFormData>>(initialData)
  const originalDeadline = useRef(
    initialData[StepType.TIME_FRAME_SELECTION]?.deadline ?? initialData[StepType.TASK_SETTINGS]?.deadline
  ).current

  const getFormData = useCallback(() => formDataRef.current, [])

  const updateFormData = useCallback(<T extends keyof TaskFormData>(step: T, data: TaskFormData[T]) => {
    const updatedData = { ...formDataRef.current, [step]: data }

    formDataRef.current = updatedData
    setFormData(updatedData)
  }, [])

  const sumbitStep = useCallback(() => {
    if (currentFormSubmit) {
      currentFormSubmit()
    }
  }, [currentFormSubmit])

  const goToPreviousStep = useCallback(() => {
    setStepStack((prev) => prev.slice(0, -1))
  }, [])

  const { submitAllForms, isSubmitting, publicationComplete } = useTaskPublication({
    getFormData,
    isEdit,
    tid,
    isGroupedByCategory,
    onClose,
  })
  const { generatePdf, isPdfGenerating } = useTaskPdf(getFormData, isVmTask)

  return (
    <FormStepContext.Provider
      value={{
        currentFormSubmit,
        setCurrentFormSubmit,
        isSubtaskAiGenerating,
        setSubtaskAiGenerating,
        formData,
        getFormData,
        updateFormData,
        stepStack,
        sumbitStep,
        goToPreviousStep,
        setStepStack,
        subtaskActions,
        setSubtaskActions,
        submitAllForms,
        generatePdf,
        isPdfGenerating,
        isMediaUploading,
        setIsMediaUploading,
        isSubmitting,
        publicationComplete,
        isEdit,
        originalDeadline,
        maxAllowedOverdueDays,
        setMaxAllowedOverdueDays,
        isVmTask,
        isAiTask,
      }}
    >
      {children}
    </FormStepContext.Provider>
  )
}

export const useFormStepContext = () => {
  const context = useContext(FormStepContext)

  if (!context) {
    throw new Error('useFormStepContext must be used within a FormStepProvider')
  }
  return context
}
